import React, {
  FC,
  useState,
  useEffect,
  FormEvent,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AgentMessage,
  AgentRequestBody,
  AgentMessageResponse,
  MiniThreadMatch,
  PaginationMetadata,
  WebRTCCallRequestedEvent,
} from '@dreamai/types';
import { groupToolMessages, ToolGroup } from '../../../utils/toolFormatting';
import api from '../../../api';
import { MessageSendErrorType } from './errors/conversationErrors';
import './thread-view.scss';
import { ReactComponent as ChatSendIcon } from '../../../icons/chat-send-icon.svg';
import { ReactComponent as LoadingRingsIcon } from '../../../icons/loading-rings.svg';
import { DirectFileUploader, FileUploadState } from './DirectFileUploader';
import { FilePreview } from './FilePreview';
import { FileUploadDropZone } from './FileUploadDropZone';
import { useFileUpload } from './useFileUpload';
import {
  generateMessageColors,
  generateToolMessageColors,
  generateColorVariants,
} from './threadUtils';
import ChatToolGroupDisplay from './ChatToolGroupDisplay';
import MultiModalContent from './MultiModalContent';
import StreamHandler from './StreamHandler';
import ThirdPartyMessageHandler from './ThirdPartyMessageHandler';
import MessageOuterWrapper from './MessageOuterWrapper';
import AsyncToolDisplay from './AsyncToolDisplay';
import CallDisplay from './CallDisplay';
import CampaignActivityNotice from './CampaignActivityNotice';
import ConfirmationNotifications from './ConfirmationNotifications';
import { usePusher } from '../../../contexts/PusherContext';
import { useNewThread } from '../../../contexts/NewThreadContext';
import {
  useThreadLoading,
  NEW_THREAD,
} from '../../../contexts/ThreadLoadingContext';
import { useFormCapture } from '../../../contexts/FormCaptureContext';
import { AxiosResponse } from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { useChatCanvas } from '../../../contexts/ChatCanvasContext';
import { useAgent } from '../../../contexts/AgentContext';
import { useThreadReads } from '../../../providers';
import { ReactComponent as DownArrowIcon } from '../../../icons/down-arrow.svg';
import { ReactComponent as StartVoiceChatIcon } from '../../../icons/start-voice-chat.svg';
import { ReactComponent as MicIcon } from '../../../icons/mic.svg';
import { ReactComponent as CallingIcon } from '../../../icons/webrtc-calls/calling.svg';
import WebRTCVoiceChat from '../../WebRTCVoiceChat';
import MentionTextarea, { MentionedUser } from './MentionTextarea';
import MentionAccessModal from './MentionAccessModal';

interface ThreadViewProps {
  messages: AgentMessage[];
  threadId?: string | null;
  agentId?: string;
  agentName?: string;
  agentNickname?: string;
  predominantColour?: string;
  onMessageSent: (threadId: string, responseMessages?: AgentMessage[]) => void;
  onCreateError: (
    errorType: MessageSendErrorType,
    errorMessage: string,
    failedMessage?: string,
  ) => void;
  recoveryMessage?: string;
  onRecoveryMessageUsed?: () => void;
  setMessages: (
    messages:
      | AgentMessage[]
      | ((prevMessages: AgentMessage[]) => AgentMessage[]),
  ) => void;
  fetchThreadMessages?: (threadId: string) => Promise<void>;
  loadMoreMessages?: (
    threadId: string,
    offset: number,
    limit?: number,
  ) => Promise<PaginationMetadata | undefined>;
  paginationState?: {
    hasMore: boolean;
    total: number;
    offset: number;
    loading: boolean;
  };
  inMiniMode?: boolean;
  discussingObject?: string;
  discussingObjectMatch?: MiniThreadMatch;
  hasWritePermission?: boolean;
  isArchivedThread?: boolean;
}

/**
 * Extracts image and file URLs from uploaded files
 * @param files - Array of uploaded files
 * @returns Object containing arrays of image and file URLs
 */
const extractFileUrls = (
  files: FileUploadState[],
): { imageUrls: string[]; fileUrls: string[] } => {
  const imageUrls: string[] = [];
  const fileUrls: string[] = [];

  files.forEach((file) => {
    if (file.url) {
      if (file.type.startsWith('image/')) {
        imageUrls.push(file.url);
      } else {
        fileUrls.push(file.url);
      }
    }
  });

  return { imageUrls, fileUrls };
};

/**
 * Formats a timestamp into a readable date string
 * Uses "Today", "Yesterday", or "Day, DD Month" format
 */
const formatDateDivider = (timestamp: number): string => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Reset time to midnight for comparison
  const resetTime = (d: Date) => {
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const dateNoTime = resetTime(new Date(date));
  const todayNoTime = resetTime(new Date(today));
  const yesterdayNoTime = resetTime(new Date(yesterday));

  if (dateNoTime.getTime() === todayNoTime.getTime()) {
    return 'Today';
  } else if (dateNoTime.getTime() === yesterdayNoTime.getTime()) {
    return 'Yesterday';
  } else {
    // Format as "Monday, 14 November"
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    };
    return date.toLocaleDateString('en-GB', options);
  }
};

/**
 * Checks if two timestamps are on different days
 */
const isDifferentDay = (timestamp1: number, timestamp2: number): boolean => {
  const date1 = new Date(timestamp1);
  const date2 = new Date(timestamp2);

  return (
    date1.getDate() !== date2.getDate() ||
    date1.getMonth() !== date2.getMonth() ||
    date1.getFullYear() !== date2.getFullYear()
  );
};

/**
 * Formats a timestamp into a readable time string
 * Returns format like "3:45 PM"
 */
const formatMessageTime = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Creates a message content structure for the agent message
 * @param message - The text message content
 * @param uploadedFiles - Array of successfully uploaded files
 * @returns String message or array of content items for multi-modal messages
 */
const createMessageContent = (
  message: string,
  uploadedFiles: FileUploadState[],
): string | Array<Record<string, any>> => {
  // If no uploaded files, just return the message text
  if (!uploadedFiles || uploadedFiles.length === 0) {
    return message;
  }

  // Initialize as array with text content
  const contentArray: Array<Record<string, any>> = [
    { type: 'text', text: message },
  ];

  // Add images and files to the content array
  uploadedFiles.forEach((file) => {
    if (file.url) {
      if (file.type.startsWith('image/')) {
        // Add image content
        contentArray.push({
          type: 'image_url',
          image_url: {
            url: file.url,
          },
        });
      } else {
        // Add file content
        const filename = file.name || 'File';
        contentArray.push({
          type: 'file',
          url: file.url,
          mime_type: file.type,
          metadata: {
            filename: filename,
          },
        });
      }
    }
  });

  return contentArray;
};

const ThreadView: FC<ThreadViewProps> = ({
  messages,
  threadId,
  agentId,
  agentName,
  agentNickname,
  predominantColour,
  onMessageSent,
  onCreateError,
  recoveryMessage,
  onRecoveryMessageUsed,
  setMessages,
  fetchThreadMessages,
  loadMoreMessages,
  paginationState = {
    hasMore: false,
    total: 0,
    offset: 0,
    loading: false,
  },
  inMiniMode = false,
  discussingObject,
  discussingObjectMatch,
  hasWritePermission = true,
  isArchivedThread = false,
}) => {
  const { user } = useAuth0();
  const currentUserId = user?.sub;
  const [newMessage, setNewMessage] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<FileUploadState[]>([]);
  const [isAudioMode, setIsAudioMode] = useState(false);
  const [shouldHangupCall, setShouldHangupCall] = useState(false);
  const [isAudioConnected, setIsAudioConnected] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [showCallPrompt, setShowCallPrompt] = useState(false);
  const [callPromptMessage, setCallPromptMessage] = useState<string | null>(
    null,
  );
  const [callPromptAgentName, setCallPromptAgentName] = useState<string | null>(
    null,
  );
  const [outputLevel, setOutputLevel] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [confirmationsExpanded, setConfirmationsExpanded] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const scrollLockedRef = useRef(false);
  const [newMessagesCount, setNewMessagesCount] = useState(0);
  const [mentions, setMentions] = useState<MentionedUser[]>([]);
  const [showMentionAccessModal, setShowMentionAccessModal] = useState(false);
  const [usersNeedingAccess, setUsersNeedingAccess] = useState<MentionedUser[]>(
    [],
  );
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [pendingMentions, setPendingMentions] = useState<MentionedUser[]>([]);
  const { newThreadId, setNewThreadId } = useNewThread();
  const {
    loadingThreads,
    abortingThreads,
    setThreadLoading,
    setThreadAborting,
    registerAgentErrorCallback,
    unregisterAgentErrorCallback,
    registerWebRTCCallRequestedCallback,
    unregisterWebRTCCallRequestedCallback,
    dismissWebRTCCall,
    acceptWebRTCCall,
    getActiveWebRTCCall,
    clearActiveWebRTCCall,
  } = useThreadLoading();
  const { getFormValues } = useFormCapture();
  const { chatCanvasRef } = useChatCanvas();
  const { agents } = useAgent();

  // Handle file upload state updates
  const handleFileUpload = useCallback((fileState: FileUploadState) => {
    setUploadedFiles((prevFiles) => {
      // If the file already exists in the array, update it
      const fileIndex = prevFiles.findIndex((file) => file.id === fileState.id);
      if (fileIndex !== -1) {
        const newFiles = [...prevFiles];
        newFiles[fileIndex] = fileState;
        return newFiles;
      }
      // Otherwise add it to the array
      return [...prevFiles, fileState];
    });
  }, []);

  // Get file upload cleanup function
  const { cleanupPreviews } = useFileUpload(handleFileUpload);

  // Find the agent data once for the entire thread
  const agent = agents.find((a) => a.id === agentId);

  // Generate color variants once for the entire thread
  const colorVariants = predominantColour
    ? generateColorVariants(predominantColour)
    : null;

  // Get URL params - im=true shows internal messages (SYSTEM_MESSAGE, SYSTEM_MANAGER, FRONTEND_HIDE_MESSAGE)
  const [searchParams] = useSearchParams();
  const showInternalMessages = searchParams.get('im') === 'true';

  // Check if user has sent any messages (excluding system messages)
  const hasUserSentMessages = useMemo(() => {
    return messages.some((message) => {
      if (message.role !== 'user') return false;

      // Check if content is a string (simple content)
      if (typeof message.content === 'string') {
        // Show internal messages if im=true URL param is set
        if (showInternalMessages) return true;
        return (
          !message.content.startsWith('SYSTEM_MESSAGE') &&
          !message.content.startsWith('SYSTEM_MANAGER') &&
          !message.content.startsWith('FRONTEND_HIDE_MESSAGE')
        );
      }

      return true;
    });
  }, [messages, showInternalMessages]);

  // Use newThreadId as a fallback when threadId is null
  const threadIdSafe = threadId || newThreadId || null;

  // Check loading state for both threadId and new threads

  const loading = threadIdSafe
    ? !!loadingThreads[threadIdSafe]
    : !!loadingThreads[NEW_THREAD];
  const aborting = threadIdSafe
    ? !!abortingThreads[threadIdSafe]
    : !!abortingThreads[NEW_THREAD];

  // Use the appropriate thread ID for setting loading/aborting states
  const setLoading = (isLoading: boolean) =>
    setThreadLoading(threadIdSafe, isLoading);
  const setAborting = (isAborting: boolean) =>
    setThreadAborting(threadIdSafe, isAborting);

  const { markThreadAsRead } = useThreadReads();

  // Store the lastReadAt timestamp for showing divider
  // Only set if the userId in the marker matches current user
  // Using useState instead of useRef so setting it triggers a re-render
  const [lastReadAtForDivider, setLastReadAtForDivider] = useState<
    string | null
  >(null);

  // Decode readMarker from URL params on mount
  useEffect(() => {
    const readMarker = searchParams.get('read_marker');
    console.log('readMarker from URL:', readMarker);
    console.log('currentUserId:', currentUserId);

    // If no read marker in URL, clear the divider
    if (!readMarker) {
      setLastReadAtForDivider(null);
      return;
    }

    // If we have a marker but no userId yet, wait for auth to load
    if (!currentUserId) {
      console.log('Waiting for currentUserId to load...');
      return;
    }

    // Decode and set the divider
    try {
      // Decode: base64 -> JSON -> { userId, lastReadAt }
      const decoded = JSON.parse(atob(readMarker));
      console.log('Decoded readMarker:', decoded);

      // Only use if userId matches current user (prevents shared link issues)
      if (decoded.userId === currentUserId && decoded.lastReadAt) {
        setLastReadAtForDivider(decoded.lastReadAt);
        console.log('Set lastReadAtForDivider to:', decoded.lastReadAt);
      } else {
        console.log(
          'UserId mismatch or no lastReadAt:',
          decoded.userId,
          currentUserId,
        );
        setLastReadAtForDivider(null);
      }
    } catch (e) {
      console.error('Failed to decode readMarker:', e);
      setLastReadAtForDivider(null);
    }
  }, [threadId, searchParams, currentUserId]);

  // Debounced mark as read function to prevent excessive API calls
  const debouncedMarkAsReadRef = useRef<NodeJS.Timeout | null>(null);
  const debouncedMarkAsRead = useCallback(
    (threadId: string) => {
      if (debouncedMarkAsReadRef.current) {
        clearTimeout(debouncedMarkAsReadRef.current);
      }
      debouncedMarkAsReadRef.current = setTimeout(() => {
        markThreadAsRead(threadId);
      }, 1000); // 1 second debounce
    },
    [markThreadAsRead],
  );

  // Clean up uploaded files, preview URLs, and mentions when switching threads
  useEffect(() => {
    setUploadedFiles([]);
    setMentions([]);
    cleanupPreviews();
  }, [threadId, cleanupPreviews]);

  // Check for active WebRTC call when navigating to thread
  useEffect(() => {
    if (threadId && threadId !== NEW_THREAD) {
      const activeCall = getActiveWebRTCCall(threadId);
      if (activeCall) {
        console.log(
          'ThreadView: Found active call for thread',
          threadId,
          activeCall,
        );
        setShowCallPrompt(true);
        setCallPromptMessage(activeCall.message || null);
        setCallPromptAgentName(activeCall.agentName || null);
      } else {
        // Clear call prompt when switching to thread without active call
        setShowCallPrompt(false);
        setCallPromptMessage(null);
        setCallPromptAgentName(null);
      }
    }
  }, [threadId, getActiveWebRTCCall]);

  // Mark thread as read when viewing it (initial load)
  useEffect(() => {
    // Only mark as read if we have a valid threadId (not NEW_THREAD)
    if (threadId && threadId !== NEW_THREAD) {
      markThreadAsRead(threadId);
    }
  }, [threadId, markThreadAsRead]);

  // Mark as read when messages change (new messages arrive while viewing)
  useEffect(() => {
    if (threadId && threadId !== NEW_THREAD && messages.length > 0) {
      debouncedMarkAsRead(threadId);
    }
  }, [messages.length, threadId, debouncedMarkAsRead]);

  // Mark as read when window regains focus (user returns to tab)
  useEffect(() => {
    const handleFocus = () => {
      if (threadId && threadId !== NEW_THREAD) {
        markThreadAsRead(threadId);
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
      // Cleanup debounce timeout on unmount
      if (debouncedMarkAsReadRef.current) {
        clearTimeout(debouncedMarkAsReadRef.current);
      }
    };
  }, [threadId, markThreadAsRead]);

  // Pre-fill message from URL param on first load
  useEffect(() => {
    const messageParam = searchParams.get('message');
    if (messageParam && !newMessage) {
      setNewMessage(messageParam);
    }
  }, [searchParams]);

  // Note: ThreadLoadingContext now automatically registers threads from URL and NewThreadContext

  // No longer need optimisticLoading state as we use NEW_THREAD key
  const [threadIdForAbort, setThreadIdForAbort] = useState<string | null>(null);
  const [lastSubmittedMessage, setLastSubmittedMessage] = useState('');
  const [resetStreamHandlerErrorFlag, setResetStreamHandlerErrorFlag] =
    useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lastMessageRef = React.useRef<string>('');

  const { getAccessTokenSilently } = useAuth0();
  const { ensureConnection } = usePusher(true);

  // Memoize the onMessageSent callback to prevent unnecessary reconnections in StreamHandler
  const memoizedOnMessageSent = useCallback(
    (threadId: string, responseMessages?: AgentMessage[]) => {
      onMessageSent(threadId, responseMessages);
    },
    [onMessageSent],
  );

  // Track if this is the first scroll
  const isFirstScrollRef = React.useRef(true);

  // Function to scroll to bottom of messages
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current && !scrollLockedRef.current) {
      // Use instant scrolling for first load, smooth scrolling after that
      messagesEndRef.current.scrollIntoView({
        behavior: isFirstScrollRef.current ? 'auto' : 'smooth',
      });

      // After first scroll, set flag to false
      if (isFirstScrollRef.current) {
        isFirstScrollRef.current = false;
      }
    } else {
      console.log('Scroll blocked - scrollLockedRef:', scrollLockedRef.current);
    }
  }, [messagesEndRef]);

  // Scroll to bottom when messages change (but not during pagination)
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Function to handle scroll to bottom button click
  const handleScrollToBottomClick = useCallback(() => {
    scrollLockedRef.current = false;
    setNewMessagesCount(0);
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Detect when user scrolls to bottom to unlock auto-scroll
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const isAtBottom =
      element.scrollHeight - element.scrollTop <= element.clientHeight + 10; // 10px threshold

    if (isAtBottom && scrollLockedRef.current) {
      scrollLockedRef.current = false;
      setNewMessagesCount(0);
    }
  }, []);

  // Function to clean up after a send failure - memoized to prevent unnecessary reconnections
  const sendFailCleanup = useCallback(() => {
    // Remove the optimistic message
    setMessages((prevMessages: AgentMessage[]): AgentMessage[] =>
      prevMessages.slice(0, -1),
    );
    // Reset aborting state if it was set
    setAborting(false);
  }, [setMessages]);

  // Handler for when abort succeeds
  const handleAbortSucceeded = useCallback(() => {
    console.log('ThreadView: Abort succeeded');
    setAborting(false);
    setLoading(false);

    // If this is a new thread (no thread_id in URL), we need to update the URL
    // since we won't reach the AGENT_COMPLETE event
    if (threadIdForAbort && !threadId && onMessageSent) {
      console.log(
        'ThreadView: Calling onMessageSent for new aborted thread',
        threadIdForAbort,
      );
      onMessageSent(threadIdForAbort, []);
    }
  }, [threadIdForAbort, threadId, onMessageSent]);

  // Handler for agent errors
  const handleAgentError = useCallback(
    (threadId: string) => {
      console.log('ThreadView: Agent error occurred', threadId);

      // If this is a new thread (no thread_id in URL) and we have a thread ID in the data,
      // we need to update the URL since we won't reach the AGENT_COMPLETE event
      if (
        !searchParams.get('thread_id') &&
        threadId === threadIdForAbort &&
        threadIdForAbort &&
        onMessageSent
      ) {
        console.log('ThreadView: Updating URL with error thread ID', threadId);
        onMessageSent(threadIdForAbort, []);
      }
    },
    [searchParams, onMessageSent, threadIdForAbort],
  );

  // Function to abort the streaming response
  const handleAbortStreaming = useCallback(async () => {
    // Use threadIdSafe if available, otherwise fall back to threadIdForAbort
    const abortThreadId = threadIdSafe || threadIdForAbort;
    if (!abortThreadId) return;

    try {
      setAborting(true);

      const token = await getAccessTokenSilently();
      await api.post(
        `/agents/thread/${abortThreadId}/abort`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // If this is a new thread (no thread_id in URL), we need to update the URL
      // since we won't reach the AGENT_COMPLETE event

      if (!searchParams.get('thread_id') && onMessageSent) {
        console.log(
          'ThreadView: Updating URL with aborted thread ID',
          abortThreadId,
        );
        onMessageSent(abortThreadId, []);
      }

      // We don't immediately clear the loading state here
      // It will be cleared when we receive the agent-complete event via Pusher
    } catch (error) {
      console.error('Error aborting streaming:', error);
      // If abort fails, clear aborting state
      setAborting(false);
    }
  }, [
    threadIdSafe,
    threadIdForAbort,
    onMessageSent,
    getAccessTokenSilently,
    setAborting,
    setLoading,
  ]);

  // Function to recover the message text when processing fails
  const recoverFromProcessingError = useCallback(() => {
    // First try to use the ref (synchronous update)
    if (lastMessageRef.current) {
      setNewMessage(lastMessageRef.current);
      lastMessageRef.current = '';
      return;
    }

    // Fall back to the state if ref is empty
    if (lastSubmittedMessage) {
      setNewMessage(lastSubmittedMessage);
      // Clear the stored message after restoring
      setLastSubmittedMessage('');
    }
  }, [lastSubmittedMessage, setNewMessage, setLastSubmittedMessage]);

  // Combined error handler that both cleans up the failed message and recovers the text
  const handleProcessingError = useCallback(() => {
    // Important: Call recoverFromProcessingError first to restore the message
    // before sendFailCleanup removes the optimistic message
    recoverFromProcessingError();
    sendFailCleanup();
  }, [recoverFromProcessingError, sendFailCleanup]);

  // Handle recovery from error - agent continued processing after error cleanup was done
  // This reverses the error cleanup by putting the message back in chat
  const handleRecoverFromError = useCallback(() => {
    console.log(
      'ThreadView: Recovering from error - restoring message to chat',
    );
    // If there's content in the textarea (from error recovery), put it back in chat
    if (newMessage) {
      const restoredMessage: AgentMessage = {
        role: 'user',
        content: newMessage,
        timestamp: Date.now(),
      };
      setMessages((prevMessages) => [...prevMessages, restoredMessage]);
      setNewMessage(''); // Clear the textarea
    }
  }, [newMessage, setMessages, setNewMessage]);

  // Track the most recent message timestamp when pagination starts
  const lastMessageTimestampRef = useRef<number | null>(null);

  // Function to handle loading more messages
  const handleLoadMoreMessages = useCallback(async () => {
    if (!threadId || !loadMoreMessages || paginationState.loading) return;

    // Lock scrolling FIRST, before any async operations
    scrollLockedRef.current = true;
    console.log('Scroll locked before loading messages');

    // Store the timestamp of the most recent message before loading
    const mostRecentMessage = messages[messages.length - 1];
    const timestamp = mostRecentMessage?.timestamp;
    lastMessageTimestampRef.current =
      typeof timestamp === 'number'
        ? timestamp
        : typeof timestamp === 'string'
          ? parseInt(timestamp, 10)
          : Date.now();

    try {
      const currentMessageCount = messages.length;
      await loadMoreMessages(threadId, currentMessageCount, 20);
    } catch (error) {
      console.error('Error loading more messages:', error);
      scrollLockedRef.current = false;
    }
  }, [threadId, loadMoreMessages, paginationState.loading, messages]);

  // Track message count changes when scroll is locked to count new messages
  const previousMessageCountRef = useRef(messages.length);

  useEffect(() => {
    if (
      scrollLockedRef.current &&
      messages.length > previousMessageCountRef.current &&
      lastMessageTimestampRef.current !== null
    ) {
      // Count only messages with timestamps newer than when pagination started
      const newMessages = messages.filter((message) => {
        if (!message.timestamp || !lastMessageTimestampRef.current)
          return false;
        const messageTime =
          typeof message.timestamp === 'number'
            ? message.timestamp
            : typeof message.timestamp === 'string'
              ? parseInt(message.timestamp, 10)
              : 0;

        return messageTime > lastMessageTimestampRef.current;
      });
      const newMessageCount = newMessages.length;

      if (newMessageCount > 0) {
        setNewMessagesCount((prev) => prev + newMessageCount);
      }
    }
    previousMessageCountRef.current = messages.length;
  }, [messages.length]);

  const handleSendMessage = async (
    message: string,
    mentionsOverride?: MentionedUser[],
  ) => {
    // Get successfully uploaded files
    const successfulUploads = uploadedFiles.filter(
      (file) => file.status === 'success',
    );
    // Store the message for potential recovery if processing fails
    setLastSubmittedMessage(message);
    lastMessageRef.current = message;

    // Clear uploaded files after sending
    setUploadedFiles([]);

    // Toggle the reset error flag to clear any error state in StreamHandler
    setResetStreamHandlerErrorFlag((prev: boolean) => !prev);

    // Create optimistic message with multi-modal content
    const messageContent = createMessageContent(message, successfulUploads);

    const optimisticMessage: AgentMessage = {
      role: 'user',
      content: messageContent,
    };

    // Add optimistic message to parent's state
    setMessages([...messages, optimisticMessage]);

    // Scroll to bottom immediately after adding the optimistic message
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 0);

    // Use override if provided (from grant access flow), otherwise capture current state
    const mentionsToSend = mentionsOverride
      ? [...mentionsOverride]
      : [...mentions];

    // Clear input field and mentions immediately
    setNewMessage('');
    setMentions([]);

    // Resize the textarea after clearing
    setTimeout(() => {
      if (textareaRef.current) {
        adjustTextareaHeight(textareaRef.current);
      }
    }, 0);

    try {
      setLoading(true); // Will use threadId or NEW_THREAD key

      // Ensure Pusher connection is active before sending the message
      // This will reconnect if needed and wait for connection to establish
      console.log('Ensuring Pusher connection before sending message...');
      await ensureConnection();

      const token = await getAccessTokenSilently();

      // Extract image and file URLs from successful uploads
      const { imageUrls, fileUrls } = extractFileUrls(successfulUploads);

      // Get current form values for collaborative editing
      const formData = threadId ? getFormValues(threadId) : null;

      // Get the current AI reporting tree from ChatCanvas
      const tree = chatCanvasRef.current?.getTree() || [];

      // Build mentions array for request
      const mentionsForRequest =
        mentionsToSend.length > 0
          ? mentionsToSend.map((m) => ({
              userId: m.userId,
              displayName: m.displayName,
            }))
          : undefined;

      const requestBody: AgentRequestBody = {
        message,
        ...(threadId && { thread_id: threadId }),
        ...(agentId && { agent_id: agentId }),
        ...(imageUrls.length > 0 && { image_urls: imageUrls }),
        ...(fileUrls.length > 0 && { file_urls: fileUrls }),
        ...(formData?.values && { form_values: formData.values }),
        ...(formData?.id && { form_id: formData.id }),
        ...(tree.length > 0 && { tree }),
        ...(discussingObject && { discussing_object: discussingObject }),
        ...(discussingObjectMatch && {
          discussing_object_match: discussingObjectMatch,
        }),
        ...(mentionsForRequest && { mentions: mentionsForRequest }),
      };

      let response: AxiosResponse<AgentMessageResponse>;
      try {
        response = await api.post<AgentMessageResponse>(
          '/agents',
          requestBody,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        // Store the thread ID for potential abort
        if (response.data.thread_id) {
          setThreadIdForAbort(response.data.thread_id);
        }
      } catch (error) {
        // Network error or other exception
        // Revert optimistic update
        sendFailCleanup();

        setLoading(false);

        if (onCreateError) {
          onCreateError(
            MessageSendErrorType.POSSIBLE_NETWORK_ERROR,
            error instanceof Error ? error.message : 'Network error occurred',
            message,
          );
        }
        return;
      }

      if (response.data.success) {
        // If streaming is enabled, we'll get updates via Pusher
        // The loading state will be cleared when we receive the agent-complete event
        if (!response.data.streaming) {
          // If not streaming, handle the response as before
          setLoading(false);

          // Get the agent's response messages
          const responseMessages = response.data.messages || [];

          // Only handle existing threads here
          // For new threads with streaming, StreamHandler will handle it
          if (threadId) {
            // If continuing an existing conversation, notify parent with response messages
            onMessageSent(threadId, responseMessages);
          }
        }

        // Only call onMessageSent for new threads if streaming is NOT enabled
        // When streaming is enabled, StreamHandler will handle this
        if (!threadId && response.data.thread_id) {
          const newId = response.data.thread_id;
          setNewThreadId(newId);
          setThreadLoading(newId, true);
        }
      } else {
        // Revert optimistic update on error by removing the last message
        sendFailCleanup();

        setLoading(false);

        if (onCreateError) {
          onCreateError(
            MessageSendErrorType.UNSUCCESSFUL,
            response.data.error || 'Failed to send message',
            message,
          );
        }
      }
    } catch (error) {
      // Revert optimistic update
      sendFailCleanup();

      setLoading(false);

      console.error('Error sending message:', error);
      if (onCreateError) {
        onCreateError(
          MessageSendErrorType.UNKNOWN,
          error instanceof Error ? error.message : 'An error occurred',
          message,
        );
      }
    }
  };

  // Effect to handle recovery message when it changes
  useEffect(() => {
    // Only process the recovery message if it exists
    if (recoveryMessage) {
      setNewMessage(recoveryMessage);

      // Use a timeout to avoid potential re-render issues
      // This ensures the state update happens after the current render cycle
      setTimeout(() => {
        if (onRecoveryMessageUsed) {
          onRecoveryMessageUsed();
        }
      }, 0);
    }
  }, [recoveryMessage]);

  // Register and unregister agent error callback
  useEffect(() => {
    const callbackId = 'thread-view-agent-error';

    // Register the callback
    registerAgentErrorCallback(callbackId, handleAgentError);

    // Clean up on unmount
    return () => {
      unregisterAgentErrorCallback(callbackId);
    };
  }, [
    registerAgentErrorCallback,
    unregisterAgentErrorCallback,
    handleAgentError,
  ]);

  // Register WebRTC call requested callback
  useEffect(() => {
    if (!threadIdSafe || threadIdSafe === NEW_THREAD) return;

    const callbackId = `webrtc-call-${threadIdSafe}`;

    const handleCallRequested = (
      eventThreadId: string,
      data: WebRTCCallRequestedEvent,
    ) => {
      if (eventThreadId === threadIdSafe) {
        setCallPromptMessage(data.message || null);
        setCallPromptAgentName(data.agentName || null);
        setShowCallPrompt(true);
      }
    };

    registerWebRTCCallRequestedCallback(callbackId, handleCallRequested);

    return () => {
      unregisterWebRTCCallRequestedCallback(callbackId);
    };
  }, [
    threadIdSafe,
    registerWebRTCCallRequestedCallback,
    unregisterWebRTCCallRequestedCallback,
  ]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Allow sending if there's a message or if there are successfully uploaded files
    const hasSuccessfulUploads = uploadedFiles.some(
      (file) => file.status === 'success',
    );
    if ((!newMessage.trim() && !hasSuccessfulUploads) || loading) return;

    // Check if any mentioned users need access (don't have WRITE, ADMIN, or OWNER)
    const needAccess = mentions.filter((m) => {
      const perm = m.currentPermission;
      return !perm || perm === 'NONE' || perm === 'READ';
    });

    if (needAccess.length > 0 && threadId) {
      // Show confirmation modal - save ALL mentions, not just those needing access
      setUsersNeedingAccess(needAccess);
      setPendingMessage(newMessage);
      setPendingMentions([...mentions]); // Save all mentions for sending
      setShowMentionAccessModal(true);
      return;
    }

    try {
      await handleSendMessage(extractPlainText(newMessage));
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      if (onCreateError) {
        onCreateError(
          MessageSendErrorType.UNKNOWN,
          error instanceof Error ? error.message : 'An error occurred',
        );
      }
    }
  };

  // Grant access to mentioned users and send the message
  const handleGrantAccessAndSend = async () => {
    if (!threadId || !pendingMessage) return;

    setLoading(true);
    setShowMentionAccessModal(false);

    try {
      const token = await getAccessTokenSilently();

      // Grant write access to users needing it
      const shares = usersNeedingAccess.map((user) => ({
        user_id: user.userId,
        permission_level: 'WRITE',
      }));

      await api.put(
        `/agents/threads/${threadId}/shares`,
        { shares },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Send the message after granting access, passing mentions directly
      await handleSendMessage(
        extractPlainText(pendingMessage),
        pendingMentions,
      );
      setPendingMessage(null);
      setPendingMentions([]);
      setUsersNeedingAccess([]);
    } catch (error) {
      console.error('Error granting access or sending:', error);
      // Recover the message and ALL mentions so user can try again
      setNewMessage(pendingMessage);
      setMentions(pendingMentions);
      setPendingMessage(null);
      setPendingMentions([]);
      setUsersNeedingAccess([]);
      setLoading(false);
      if (onCreateError) {
        onCreateError(
          MessageSendErrorType.UNKNOWN,
          error instanceof Error ? error.message : 'Failed to grant access',
        );
      }
    }
  };

  // Handle textarea height adjustment
  const adjustTextareaHeight = (element: HTMLTextAreaElement) => {
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
    element.style.minHeight = 'auto';
  };

  // Handle input change (for MentionTextarea)
  const handleMentionChange = useCallback(
    (value: string, newMentions: MentionedUser[]) => {
      setNewMessage(value);
      setMentions(newMentions);
    },
    [],
  );

  // Extract plain text from mention markup for sending
  const extractPlainText = (value: string): string => {
    // Convert @[Name](userId) to @Name
    return value.replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1');
  };

  // Handle key press in textarea
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // If Enter is pressed without Shift, submit the form
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();

      // Allow sending if there's a message or if there are successfully uploaded files
      const hasSuccessfulUploads = uploadedFiles.some(
        (file) => file.status === 'success',
      );
      if ((newMessage.trim() || hasSuccessfulUploads) && !loading) {
        handleSubmit(e as unknown as FormEvent);
      }
    }
  };

  // Handle file removal
  const handleRemoveFile = (fileId: string) => {
    setUploadedFiles((prevFiles) =>
      prevFiles.filter((file) => file.id !== fileId),
    );
  };

  // Handle toggle recording
  const toggleRecording = async () => {
    if (isRecording) {
      // Stop recording
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
      }
    } else {
      // Start recording
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        const mediaRecorder = new MediaRecorder(stream, {
          mimeType: 'audio/webm',
        });

        mediaRecorderRef.current = mediaRecorder;
        audioChunksRef.current = [];

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = async () => {
          // Stop all tracks
          stream.getTracks().forEach((track) => track.stop());

          // Process the recording
          const audioBlob = new Blob(audioChunksRef.current, {
            type: 'audio/webm',
          });
          await transcribeAudio(audioBlob);
        };

        mediaRecorder.start();
        setIsRecording(true);
      } catch (error) {
        console.error('Error starting recording:', error);
        alert('Could not access microphone. Please check your permissions.');
      }
    }
  };

  const transcribeAudio = async (audioBlob: Blob) => {
    setIsTranscribing(true);

    try {
      const token = await getAccessTokenSilently();
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const response = await api.post('/voice-transcribe', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success && response.data.transcription) {
        // Append transcription to existing message (allows recording in parts)
        setNewMessage((prevMessage) => {
          const separator = prevMessage.trim() ? ' ' : '';
          return prevMessage + separator + response.data.transcription;
        });

        // Resize the textarea and auto-focus
        setTimeout(() => {
          if (textareaRef.current) {
            adjustTextareaHeight(textareaRef.current);
            textareaRef.current.focus();
          }
        }, 0);
      } else {
        console.error('Transcription failed:', response.data);
        alert('Failed to transcribe audio. Please try again.');
      }
    } catch (error) {
      console.error('Error transcribing audio:', error);
      alert('Error transcribing audio. Please try again.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleDeclineCall = async () => {
    setShowCallPrompt(false);

    // Notify other components (like CallNotification) that the call was dismissed
    if (threadIdSafe) {
      dismissWebRTCCall(threadIdSafe);
    }

    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/webrtc/decline-call',
        {
          thread_id: threadIdSafe,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
    } catch (error) {
      console.error('Error declining call:', error);
      // Silent fail - user already dismissed the prompt
    }
  };

  return (
    <div
      className={`thread-view${inMiniMode ? ' mini-view' : ''} ${discussingObject === 'fast-chat' ? 'fast-chat' : ''}`}
    >
      <div className='thread-messages' onScroll={handleScroll}>
        {messages.length === 0 ? (
          <div className='no-messages'>
            {discussingObject === 'fast-chat' && (
              <div className='fast-chat-empty-state'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='32'
                  height='32'
                  viewBox='0 0 32 32'
                  fill='none'
                >
                  <path
                    d='M17.3332 2.66663L5.45784 16.9171C4.99276 17.4752 4.76022 17.7542 4.75667 17.9899C4.75358 18.1948 4.84487 18.3897 5.00424 18.5185C5.18756 18.6666 5.5508 18.6666 6.27728 18.6666H15.9999L14.6666 29.3333L26.542 15.0828C27.007 14.5247 27.2396 14.2457 27.2431 14.01C27.2462 13.8051 27.1549 13.6102 26.9956 13.4814C26.8122 13.3333 26.449 13.3333 25.7225 13.3333H15.9999L17.3332 2.66663Z'
                    stroke='#838B9B'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
                <p className='leader'>
                  Use quick chat to get answers, check spelling, or just find
                  out the weather.
                </p>
                <p className='follower'>
                  Your quick chat doesn't connect to your Dreamies.
                </p>
              </div>
            )}
            {/* StreamHandler must be rendered even with no messages to receive streaming events */}
            <StreamHandler
              threadId={threadIdSafe}
              messages={messages}
              setMessages={setMessages}
              onMessageSent={memoizedOnMessageSent}
              onError={handleProcessingError}
              onRecoverFromError={handleRecoverFromError}
              onAbortSucceeded={handleAbortSucceeded}
              predominantColour={predominantColour}
              onScrollNeeded={scrollToBottom}
              resetErrorOnNewMessage={resetStreamHandlerErrorFlag}
              fetchThreadMessages={fetchThreadMessages}
              messageWrapper={({ children }) => (
                <MessageOuterWrapper
                  messageRole='agent'
                  inMiniMode={inMiniMode}
                  agent={agent}
                  colorVariants={colorVariants}
                  discussingObject={discussingObject}
                >
                  {children}
                </MessageOuterWrapper>
              )}
            />
          </div>
        ) : (
          <div
            className='message-list'
            style={confirmationsExpanded ? { opacity: 0.3 } : undefined}
          >
            {/* Load more button */}
            {paginationState.hasMore && threadId && loadMoreMessages && (
              <div className='load-more-container'>
                <button
                  className={`load-more-button ${paginationState.loading ? 'loading' : ''}`}
                  onClick={handleLoadMoreMessages}
                  disabled={paginationState.loading}
                >
                  {paginationState.loading ? (
                    <>
                      Loading
                      <LoadingRingsIcon />
                    </>
                  ) : (
                    'Load more messages'
                  )}
                </button>
              </div>
            )}
            {(() => {
              // Group and filter messages
              const groupedAndFilteredMessages = groupToolMessages(
                messages.filter((message) => {
                  // Filter out messages hidden from user
                  if (message.metadata?.hiddenFromUser) return false;

                  // Filter out tool calls from hidden families (e.g., acknowledgeInactivity)
                  if (message.role === 'tool') {
                    const hiddenToolFamilies = ['acknowledgeInactivity'];
                    if (
                      typeof message.content === 'string' &&
                      hiddenToolFamilies.includes(message.content)
                    ) {
                      return false;
                    }
                  }

                  // Filter out system messages (user messages that start with the system prefix)
                  if (message.role !== 'user') return true;

                  // Check if content is a string (simple content)
                  if (typeof message.content === 'string') {
                    // Show internal messages if im=true URL param is set
                    if (showInternalMessages) return true;
                    return (
                      !message.content.startsWith('SYSTEM_MESSAGE') &&
                      !message.content.startsWith('SYSTEM_MANAGER') &&
                      !message.content.startsWith('FRONTEND_HIDE_MESSAGE')
                    );
                  }

                  return true;
                }),
              );

              // Track if we've already shown the "New replies" divider
              let dividerShown = false;
              // Track the last date we showed a divider for
              let lastDividerDate: number | null = null;
              // Track last message times for user and agent messages separately
              let lastUserMessageTime: number | null = null;
              let lastAgentMessageTime: number | null = null;

              return groupedAndFilteredMessages.map((item, index, array) => {
                // Check if this is a tool group or a regular message
                const isActive = index === array.length - 1;

                // Check if we should show the "New replies" divider before this item
                const shouldShowDivider = (() => {
                  // If we've already shown the divider, don't show again
                  if (dividerShown) {
                    return false;
                  }

                  if (!lastReadAtForDivider) {
                    console.log('No lastReadAtForDivider');
                    return false;
                  }
                  // Only skip the very first message (can't show divider before it)
                  if (index === 0) {
                    console.log(
                      `Skipping divider: index ${index} (first message)`,
                    );
                    return false;
                  }

                  const lastReadAtTime = new Date(
                    lastReadAtForDivider,
                  ).getTime();

                  // Get timestamp of current item
                  let currentItemTime: number;
                  if ('tools' in item && 'family' in item) {
                    const toolGroup = item as ToolGroup;
                    currentItemTime = toolGroup.tools[0]?.timestamp || 0;
                  } else {
                    const message = item as AgentMessage;
                    currentItemTime = message.timestamp || 0;
                  }

                  // Get timestamp of previous item
                  const prevItem = array[index - 1];
                  let prevItemTime: number;
                  if ('tools' in prevItem && 'family' in prevItem) {
                    const toolGroup = prevItem as ToolGroup;
                    prevItemTime = toolGroup.tools[0]?.timestamp || 0;
                  } else {
                    const message = prevItem as AgentMessage;
                    prevItemTime = message.timestamp || 0;
                  }

                  const result =
                    prevItemTime <= lastReadAtTime &&
                    currentItemTime > lastReadAtTime;

                  console.log('Divider check:', {
                    index,
                    lastReadAtTime,
                    prevItemTime,
                    currentItemTime,
                    result,
                  });

                  // Mark as shown if we're going to show it
                  if (result) {
                    dividerShown = true;
                  }

                  return result;
                })();

                // Check if we should show a date divider before this item
                const shouldShowDateDivider = (() => {
                  // Get timestamp of current item
                  let currentItemTime: number;
                  if ('tools' in item && 'family' in item) {
                    const toolGroup = item as ToolGroup;
                    currentItemTime = toolGroup.tools[0]?.timestamp || 0;
                  } else {
                    const message = item as AgentMessage;
                    currentItemTime = message.timestamp || 0;
                  }

                  // Skip if no valid timestamp
                  if (!currentItemTime) return false;

                  // Show date divider for first message unless it's today
                  if (index === 0) {
                    lastDividerDate = currentItemTime;
                    const today = new Date();
                    const messageDate = new Date(currentItemTime);
                    const isToday =
                      messageDate.getDate() === today.getDate() &&
                      messageDate.getMonth() === today.getMonth() &&
                      messageDate.getFullYear() === today.getFullYear();
                    return !isToday;
                  }

                  // Check if we need a new date divider
                  if (
                    lastDividerDate &&
                    isDifferentDay(lastDividerDate, currentItemTime)
                  ) {
                    lastDividerDate = currentItemTime;
                    return true;
                  }

                  return false;
                })();

                const dateDividerText = (() => {
                  let currentItemTime: number;
                  if ('tools' in item && 'family' in item) {
                    const toolGroup = item as ToolGroup;
                    currentItemTime = toolGroup.tools[0]?.timestamp || 0;
                  } else {
                    const message = item as AgentMessage;
                    currentItemTime = message.timestamp || 0;
                  }
                  return currentItemTime
                    ? formatDateDivider(currentItemTime)
                    : '';
                })();

                if ('tools' in item && 'family' in item) {
                  // It's a tool group
                  const toolGroup = item as ToolGroup;

                  // A tool group is only considered active if it's the very last message
                  // This indicates it's still in progress

                  return (
                    <React.Fragment
                      key={`group-${toolGroup.tools[0]?.tool_call_id || toolGroup.tools[0]?.timestamp || index}`}
                    >
                      {shouldShowDateDivider && (
                        <div className='date-divider'>
                          <span>{dateDividerText}</span>
                        </div>
                      )}
                      {shouldShowDivider && (
                        <div className='new-replies-divider'>
                          <span>New replies to this chat</span>
                        </div>
                      )}
                      <div className='message tool-message'>
                        <ChatToolGroupDisplay
                          toolGroup={toolGroup}
                          isActive={isActive}
                          colors={
                            predominantColour
                              ? generateToolMessageColors(predominantColour)
                              : undefined
                          }
                        />
                      </div>
                    </React.Fragment>
                  );
                } else {
                  // It's a regular message
                  const message = item as AgentMessage;

                  // Check if we should show timestamp for user or agent messages
                  const shouldShowTimestamp = (() => {
                    // Don't show for tool messages
                    if (message.role === 'tool') return false;

                    const currentTime = message.timestamp || 0;
                    if (!currentTime) return false;

                    // Check if this is the last message in a sequence of the same type
                    // For agent messages, skip over tool groups to find the next non-tool item
                    let nextNonToolItem = null;
                    let lookAheadIndex = index + 1;

                    while (lookAheadIndex < array.length) {
                      const item = array[lookAheadIndex];
                      if ('tools' in item && 'family' in item) {
                        // Skip tool groups for agent messages
                        if (message.role === 'agent') {
                          lookAheadIndex++;
                          continue;
                        }
                      }
                      nextNonToolItem = item;
                      break;
                    }

                    let isLastInSequence = false;

                    if (!nextNonToolItem) {
                      // This is the last message in the sequence (no more non-tool items)
                      isLastInSequence = true;
                    } else if (
                      'tools' in nextNonToolItem &&
                      'family' in nextNonToolItem
                    ) {
                      // Next non-tool item is a tool (only possible for user messages)
                      isLastInSequence = true;
                    } else {
                      // Next item is a message, check if it's a different role
                      const nextMessage = nextNonToolItem as AgentMessage;
                      isLastInSequence = nextMessage.role !== message.role;
                    }

                    const THREE_MINUTES_MS = 3 * 60 * 1000;

                    if (message.role === 'user') {
                      // Show if: last in sequence OR 3+ min gap from last shown timestamp
                      const hasTimeGap =
                        lastUserMessageTime !== null &&
                        currentTime - lastUserMessageTime >= THREE_MINUTES_MS;

                      if (isLastInSequence || hasTimeGap) {
                        lastUserMessageTime = currentTime;
                        return true;
                      }
                      return false;
                    } else if (message.role === 'agent') {
                      // Show if: last in sequence OR 3+ min gap from last shown timestamp
                      const hasTimeGap =
                        lastAgentMessageTime !== null &&
                        currentTime - lastAgentMessageTime >= THREE_MINUTES_MS;

                      if (isLastInSequence || hasTimeGap) {
                        lastAgentMessageTime = currentTime;
                        return true;
                      }
                      return false;
                    }

                    return false;
                  })();

                  const messageTimeText = message.timestamp
                    ? formatMessageTime(message.timestamp)
                    : '';

                  return (
                    <React.Fragment
                      key={`message-${message.timestamp || message.tool_call_id || index}`}
                    >
                      {shouldShowDateDivider && (
                        <div className='date-divider'>
                          <span>{dateDividerText}</span>
                        </div>
                      )}
                      {shouldShowDivider && (
                        <div className='new-replies-divider'>
                          <span>New replies to this chat</span>
                        </div>
                      )}
                      <MessageOuterWrapper
                        messageRole={message.role}
                        inMiniMode={inMiniMode}
                        agent={agent}
                        colorVariants={colorVariants}
                        discussingObject={discussingObject}
                        isSharedUserMessage={
                          message.role === 'user' &&
                          message.metadata?.userId &&
                          message.metadata.userId !== currentUserId
                        }
                      >
                        <div
                          className={`message ${
                            message.role === 'user'
                              ? message.metadata?.userId &&
                                message.metadata.userId !== currentUserId
                                ? 'user-message shared-user-message'
                                : 'user-message'
                              : message.role === 'tool'
                                ? 'tool-message'
                                : 'agent-message'
                          }`}
                        >
                          {message.role === 'tool' ? (
                            <ChatToolGroupDisplay
                              toolGroup={{ family: '', tools: [message] }}
                              isActive={isActive}
                              colors={
                                predominantColour
                                  ? generateToolMessageColors(predominantColour)
                                  : undefined
                              }
                            />
                          ) : (
                            <>
                              {message.role === 'user' &&
                                message.metadata?.userName &&
                                message.metadata.userId !== currentUserId && (
                                  <div className='message-author'>
                                    {message.metadata.userName}
                                  </div>
                                )}
                              <div className='message-content'>
                                <MultiModalContent
                                  content={message.content}
                                  metadata={message.metadata}
                                  threadId={threadId || undefined}
                                  colors={
                                    message.role !== 'user' && predominantColour
                                      ? generateMessageColors(predominantColour)
                                      : undefined
                                  }
                                />
                              </div>
                            </>
                          )}
                        </div>
                        {shouldShowTimestamp && message.role !== 'tool' && (
                          <div className='message-timestamp'>
                            {messageTimeText}
                          </div>
                        )}
                      </MessageOuterWrapper>
                    </React.Fragment>
                  );
                }
              });
            })()}

            <StreamHandler
              threadId={threadIdSafe}
              messages={messages}
              setMessages={setMessages}
              onMessageSent={memoizedOnMessageSent}
              onError={handleProcessingError}
              onRecoverFromError={handleRecoverFromError}
              onAbortSucceeded={handleAbortSucceeded}
              predominantColour={predominantColour}
              onScrollNeeded={scrollToBottom}
              resetErrorOnNewMessage={resetStreamHandlerErrorFlag}
              fetchThreadMessages={fetchThreadMessages}
              messageWrapper={({ children }) => (
                <MessageOuterWrapper
                  messageRole='agent'
                  inMiniMode={inMiniMode}
                  agent={agent}
                  colorVariants={colorVariants}
                  discussingObject={discussingObject}
                >
                  {children}
                </MessageOuterWrapper>
              )}
            />

            <CallDisplay
              threadId={threadIdSafe}
              onScrollNeeded={scrollToBottom}
              fetchThreadMessages={fetchThreadMessages}
            />

            {discussingObject?.toLowerCase() === 'campaign' &&
              discussingObjectMatch &&
              'campaignId' in discussingObjectMatch && (
                <CampaignActivityNotice
                  campaignId={discussingObjectMatch.campaignId.toString()}
                  isThreadLoading={loading}
                  hasUserSentMessages={hasUserSentMessages}
                  colorVariants={colorVariants}
                />
              )}

            <AsyncToolDisplay threadId={threadIdSafe} />

            <ThirdPartyMessageHandler
              threadId={threadIdSafe}
              setMessages={setMessages}
            />

            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Fixed scroll to bottom button */}
        {scrollLockedRef.current && newMessagesCount > 0 && (
          <div className='scroll-to-bottom-container'>
            <button
              className='scroll-to-bottom-button'
              onClick={handleScrollToBottomClick}
            >
              <span className='new-messages-count'>{newMessagesCount}</span>
              <DownArrowIcon />
            </button>
          </div>
        )}
      </div>
      {/* Show "Continue conversation in full screen" link in mini mode when there are messages */}
      {inMiniMode &&
        messages.length > 0 &&
        discussingObject !== 'fast-chat' && (
          <div className='mini-view-fullscreen-link'>
            <a
              href={`/conversations?agent_id=${agentId}&thread_id=${threadId}`}
              target='_blank'
              rel='noopener noreferrer'
            >
              Continue conversation in full screen
            </a>
          </div>
        )}
      <div className='bottom-stuck'>
        <ConfirmationNotifications
          threadId={threadIdSafe}
          inMiniMode={inMiniMode}
          predominantColour={predominantColour}
          onExpandedChange={(expanded) => setConfirmationsExpanded(expanded)}
        />
        {isArchivedThread ? (
          <div className='archived-thread-notice'>
            This thread has been archived
          </div>
        ) : (
          <FileUploadDropZone
            onFileUpload={handleFileUpload}
            disabled={!hasWritePermission || isAudioMode}
            className='dragger-wrapper'
          >
            <form
              className={`message-input-form${isAudioMode || showCallPrompt ? ' audio-mode' : ''}${isAudioConnected ? ' connected-audio' : ''}`}
              onSubmit={handleSubmit}
              style={
                {
                  '--audio-level': outputLevel,
                } as React.CSSProperties
              }
            >
              {showCallPrompt ? (
                <div className='webrtc-call-request-wrapper'>
                  <div className='call-request-topper'>
                    <CallingIcon />
                    <div className='call-request-content'>
                      <div className='agent-name'>
                        {callPromptAgentName || agentName || 'Agent'} wants to
                        talk
                      </div>
                      {callPromptMessage && (
                        <div className='call-message'>{callPromptMessage}</div>
                      )}
                    </div>
                  </div>

                  <div className='call-request-actions'>
                    <button
                      className='answer-call-button'
                      onClick={() => {
                        setIsAudioMode(true);
                        setShowCallPrompt(false);
                        // Notify other components that the call was accepted
                        if (threadIdSafe) {
                          acceptWebRTCCall(threadIdSafe);
                        }
                      }}
                    >
                      Answer
                    </button>
                    <button
                      className='dismiss-call-button'
                      onClick={handleDeclineCall}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              ) : !isAudioMode ? (
                <MentionTextarea
                  value={newMessage}
                  onChange={handleMentionChange}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    hasWritePermission
                      ? `Talk to ${agentNickname || agentName || 'Agent'}`
                      : 'You do not have permission to send messages'
                  }
                  disabled={!hasWritePermission}
                  threadId={threadId}
                />
              ) : (
                <WebRTCVoiceChat
                  agentId={agentId}
                  threadId={threadId || undefined}
                  miniMode={true}
                  autoStart={true}
                  shouldHangup={shouldHangupCall}
                  onConnectionChange={(connected) =>
                    setIsAudioConnected(connected)
                  }
                  onAudioLevelChange={(level) => setAudioLevel(level)}
                  onOutputLevelChange={(level) => setOutputLevel(level)}
                  onError={(error) => console.error('WebRTC Error:', error)}
                  onHangupComplete={() => {
                    setShouldHangupCall(false);
                    setIsAudioMode(false);
                  }}
                />
              )}

              {/* File preview area */}
              {!isAudioMode && !showCallPrompt && (
                <FilePreview
                  files={uploadedFiles}
                  onRemoveFile={handleRemoveFile}
                />
              )}

              <div className='actions-wrapper'>
                {/* DirectFileUploader component */}
                {!isAudioMode && !showCallPrompt ? (
                  <DirectFileUploader
                    onFileUpload={handleFileUpload}
                    disabled={loading || !hasWritePermission}
                  />
                ) : (
                  <div></div>
                )}
                <div className='buttons-wrapper'>
                  {!isAudioMode && !showCallPrompt && (
                    <>
                      {discussingObject !== 'fast-chat' && (
                        <div
                          className='start-voice-chat-button'
                          onClick={() => setIsAudioMode(true)}
                        >
                          <StartVoiceChatIcon />
                        </div>
                      )}
                      <div
                        className={`microphone-button ${isRecording ? 'recording' : ''} ${isTranscribing ? 'transcribing' : ''}`}
                        onClick={() => {
                          if (!hasWritePermission || loading || isTranscribing)
                            return;
                          toggleRecording();
                        }}
                        style={{
                          opacity:
                            !hasWritePermission || loading || isTranscribing
                              ? 0.5
                              : 1,
                          cursor:
                            !hasWritePermission || loading || isTranscribing
                              ? 'not-allowed'
                              : 'pointer',
                        }}
                      >
                        {isTranscribing ? (
                          <div className='loading-rings'>
                            <LoadingRingsIcon />
                          </div>
                        ) : (
                          <MicIcon />
                        )}
                      </div>
                    </>
                  )}
                  {!showCallPrompt && (
                    <>
                      {!isAudioMode ? (
                        <button
                          className='send-button'
                          disabled={
                            (!newMessage.trim() && !loading) ||
                            !hasWritePermission
                          }
                          onClick={
                            loading && !aborting
                              ? handleAbortStreaming
                              : undefined
                          }
                          type={loading && !aborting ? 'button' : 'submit'}
                        >
                          {loading ? (
                            aborting ? (
                              <div className='loading-rings'>
                                <LoadingRingsIcon />
                              </div>
                            ) : (
                              <div
                                className='stop-square'
                                style={{
                                  width: '12px',
                                  height: '12px',
                                  flexShrink: 0,
                                  aspectRatio: '1/1',
                                  borderRadius: '2px',
                                  background: 'var(--Plus-White, #FFFEFC)',
                                }}
                              ></div>
                            )
                          ) : (
                            <div className='chat-send'>
                              <ChatSendIcon />
                            </div>
                          )}
                        </button>
                      ) : (
                        <button
                          className='send-button stop-audio-button'
                          onClick={() => {
                            setShouldHangupCall(true);
                          }}
                          type='button'
                        >
                          <div
                            className='stop-square'
                            style={{
                              width: '12px',
                              height: '12px',
                              flexShrink: 0,
                              aspectRatio: '1/1',
                              borderRadius: '2px',
                              background: 'var(--Plus-White, #FFFEFC)',
                            }}
                          ></div>
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </form>
          </FileUploadDropZone>
        )}
      </div>

      <MentionAccessModal
        show={showMentionAccessModal}
        onClose={() => {
          setShowMentionAccessModal(false);
          setPendingMessage(null);
          setUsersNeedingAccess([]);
        }}
        onConfirm={handleGrantAccessAndSend}
        usersNeedingAccess={usersNeedingAccess}
      />
    </div>
  );
};

export default ThreadView;
